import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';

interface PackageInquiryModalProps {
    isOpen: boolean;
    onClose: () => void;
    packageName: string;
    packageType: string;
}

const PackageInquiryModal: React.FC<PackageInquiryModalProps> = ({ isOpen, onClose, packageName, packageType }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: ''
    });
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.mobile) {
            toast.error('Please fill name and mobile number');
            return;
        }

        setLoading(true);
        try {
            // Send inquiry to the backend
            // Using the lead endpoint since it maps to inquiries
            const payload = {
                ...formData,
                source: 'Package Inquiry',
                status: 'New Inquiry',
                notes: `Interested in ${packageName} (${packageType} package)`
            };
            
            await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/public-package-inquiry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            toast.success('Your inquiry has been submitted! Our team will contact you soon.');
            onClose();
            setFormData({ name: '', email: '', mobile: '' });
        } catch (err) {
            console.error('Inquiry submission failed:', err);
            toast.error('Failed to submit inquiry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
                <div className="relative p-6 bg-[#0F2A5F] text-white">
                    <button 
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
                    >
                        <i className="fa-solid fa-xmark text-lg"></i>
                    </button>
                    <div className="w-10 h-10 rounded-lg bg-[#D4A017]/20 flex items-center justify-center text-[#D4A017] mb-3">
                        <i className="fa-solid fa-envelope-open-text text-lg"></i>
                    </div>
                    <h3 className="text-xl font-bold mb-1 text-white">Package Inquiry</h3>
                    <p className="text-white/90 text-xs font-medium">You are interested in <span className="text-[#D4A017] font-bold">{packageName}</span></p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-[12px] font-medium text-slate-700 ml-1">Full Name *</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#D4A017] transition-colors">
                                <i className="fa-solid fa-user text-[10px]"></i>
                            </div>
                            <input 
                                type="text" 
                                required
                                placeholder="Enter your full name"
                                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm font-medium text-black outline-none focus:border-[#D4A017] focus:bg-white transition-all shadow-sm"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[12px] font-medium text-slate-700 ml-1">Mobile Number *</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#D4A017] transition-colors">
                                <i className="fa-solid fa-phone text-[10px]"></i>
                            </div>
                            <input 
                                type="tel" 
                                required
                                placeholder="Enter mobile number"
                                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm font-medium text-black outline-none focus:border-[#D4A017] focus:bg-white transition-all shadow-sm"
                                value={formData.mobile}
                                onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[12px] font-medium text-slate-700 ml-1">Email Address</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#D4A017] transition-colors">
                                <i className="fa-solid fa-envelope text-[10px]"></i>
                            </div>
                            <input 
                                type="email" 
                                placeholder="Enter your email"
                                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm font-medium text-black outline-none focus:border-[#D4A017] focus:bg-white transition-all shadow-sm"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                    </div>

                    <button 
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-[#D4A017] text-white rounded-xl text-xs font-bold uppercase tracking-widest shadow-md shadow-[#D4A017]/20 hover:bg-[#8b4513] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>Submitting...</span>
                            </div>
                        ) : (
                            'Submit Inquiry'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PackageInquiryModal;
